import React from 'react';
import { Box, Container, Typography, Chip, Divider } from '@mui/material';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';

const PRIMARY = '#FF7518';
const DARK = '#4a2d08';
const DARK_BG = '#0f0f0f';

type ChangeType = 'new' | 'improved' | 'fixed';

interface Change {
  type: ChangeType;
  text: string;
}

interface Release {
  version: string;
  date: string;
  summary: string;
  changes: Change[];
}

const releases: Release[] = [
  {
    version: '1.4.0',
    date: 'April 2026',
    summary: 'Razorpay payments, policy pages, and index improvements.',
    changes: [
      { type: 'new', text: 'Razorpay payment gateway integration for INR billing' },
      { type: 'new', text: 'Privacy Policy, Terms & Conditions, and Refund Policy pages' },
      { type: 'new', text: 'Docs page with Quick Start, CLI Reference, Rule Library, and Integrations sections' },
      { type: 'new', text: 'Careers and Contact pages' },
      { type: 'improved', text: 'Firestore indexes updated for tokens collection (scope + status + expiresAt)' },
      { type: 'improved', text: 'Login and Register UI redesigned with cleaner layout' },
      { type: 'fixed', text: 'Unused icon imports removed from homepage causing lint warnings' },
    ],
  },
  {
    version: '1.3.0',
    date: 'March 2026',
    summary: 'False positive reporting, analytics dashboard, and multi-cloud rule additions.',
    changes: [
      { type: 'new', text: 'False positive reporting — mark findings as accepted risk with justification' },
      { type: 'new', text: 'Analytics dashboard with scan trends, risk score history, and framework breakdown' },
      { type: 'new', text: '20 new ISO 27001 rules covering Annex A domains 10–14' },
      { type: 'new', text: 'GCP Healthcare API compliance rules (12 new rules)' },
      { type: 'improved', text: 'Scan details page redesigned with collapsible rule groups' },
      { type: 'improved', text: 'Dashboard overview shows 30-day scan trend chart' },
      { type: 'fixed', text: 'Scan list pagination reset correctly after filter change' },
    ],
  },
  {
    version: '1.2.0',
    date: 'February 2026',
    summary: 'Stripe billing, plan management, and FHIR R5 rules.',
    changes: [
      { type: 'new', text: 'Stripe subscription billing with monthly and annual plans' },
      { type: 'new', text: 'Plan management dashboard — upgrade, downgrade, and cancel' },
      { type: 'new', text: '15 new FHIR R5 resource validation rules' },
      { type: 'new', text: 'PDF and executive summary export for audit-ready reports' },
      { type: 'improved', text: 'CLI token page shows token expiry and rotation history' },
      { type: 'improved', text: 'Scan result now includes HIPAA regulation clause mapping' },
      { type: 'fixed', text: 'Email verification redirect loop on first login resolved' },
    ],
  },
  {
    version: '1.1.0',
    date: 'January 2026',
    summary: 'GitHub Actions integration, CI/CD support, and scan filtering.',
    changes: [
      { type: 'new', text: 'GitHub Actions workflow step for automated compliance scanning' },
      { type: 'new', text: 'Jenkins and Terraform integration guides' },
      { type: 'new', text: 'Scan list filtering by framework, risk level, and date range' },
      { type: 'new', text: '--fail-on flag to control CI pipeline exit code by severity' },
      { type: 'improved', text: 'CLI install script now supports macOS (arm64/x86), Linux, and Windows' },
      { type: 'fixed', text: 'Scan count quota correctly resets on billing period renewal' },
    ],
  },
  {
    version: '1.0.0',
    date: 'December 2025',
    summary: 'Initial public launch of BaaStream.',
    changes: [
      { type: 'new', text: 'Core compliance scanning engine with 180+ rules across HIPAA, FHIR, HITRUST, SOC 2, and GDPR' },
      { type: 'new', text: 'CLI tool with baastream scan command' },
      { type: 'new', text: 'Dashboard with scan history and result details' },
      { type: 'new', text: 'CLI token management (issue, revoke, rotate)' },
      { type: 'new', text: 'User authentication with email verification' },
      { type: 'new', text: 'Community (free), Starter, Pro, and Enterprise plans' },
    ],
  },
];

const tagColors: Record<ChangeType, { bg: string; color: string; label: string }> = {
  new:      { bg: 'rgba(22,163,74,0.1)',     color: '#16a34a', label: 'New' },
  improved: { bg: 'rgba(37,99,235,0.1)',     color: '#2563eb', label: 'Improved' },
  fixed:    { bg: 'rgba(220,38,38,0.1)',     color: '#dc2626', label: 'Fixed' },
};

export function Changelog() {
  return (
    <Box sx={{ bgcolor: '#fff', minHeight: '100vh' }}>
      {/* Minimal nav */}
      <Box sx={{ bgcolor: DARK_BG, py: 2, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <Container maxWidth="lg">
          <Box component="a" href="/" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, textDecoration: 'none' }}>
            <HealthAndSafetyIcon sx={{ color: PRIMARY, fontSize: 26 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff' }}>BaaStream</Typography>
          </Box>
        </Container>
      </Box>

      {/* Hero */}
      <Box sx={{ bgcolor: DARK_BG, py: { xs: 8, md: 12 } }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Chip label="Changelog" size="small" sx={{ bgcolor: 'rgba(255,117,24,0.15)', color: PRIMARY, fontWeight: 600, mb: 3 }} />
          <Typography variant="h1" sx={{ fontSize: { xs: '2.2rem', md: '3rem' }, fontWeight: 800, color: '#fff', lineHeight: 1.2, mb: 3 }}>
            What's{' '}
            <Box component="span" sx={{ color: PRIMARY }}>new</Box>
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '1.05rem', lineHeight: 1.8 }}>
            Every release, every fix, every improvement — documented here.
          </Typography>
        </Container>
      </Box>

      {/* Releases */}
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 10 } }}>
        {releases.map((release, idx) => (
          <Box key={release.version}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, mb: 1 }}>
              <Box sx={{ minWidth: 80, textAlign: 'right', pt: 0.5 }}>
                <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>{release.date}</Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <Chip
                    label={`v${release.version}`}
                    size="small"
                    sx={{ bgcolor: idx === 0 ? PRIMARY : '#f0f0f0', color: idx === 0 ? '#fff' : DARK, fontWeight: 700 }}
                  />
                  {idx === 0 && (
                    <Chip label="Latest" size="small" sx={{ bgcolor: 'rgba(22,163,74,0.12)', color: '#16a34a', fontWeight: 600 }} />
                  )}
                </Box>
                <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>{release.summary}</Typography>
                <Box>
                  {release.changes.map((change, i) => {
                    const tag = tagColors[change.type];
                    return (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.25 }}>
                        <Chip
                          label={tag.label}
                          size="small"
                          sx={{ bgcolor: tag.bg, color: tag.color, fontWeight: 600, fontSize: '0.7rem', height: 20, minWidth: 68 }}
                        />
                        <Typography variant="body2" sx={{ color: '#444', lineHeight: 1.6 }}>{change.text}</Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>
            {idx < releases.length - 1 && <Divider sx={{ my: 5 }} />}
          </Box>
        ))}
      </Container>

      {/* Footer */}
      <Box sx={{ bgcolor: DARK_BG, py: 4, mt: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>© {new Date().getFullYear()} Monks Do Work. All rights reserved.</Typography>
            <Box component="a" href="/" sx={{ color: PRIMARY, fontSize: '0.8rem', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>← Back to Home</Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
